// src/components/screens/ChatScreen/index.jsx
import React, { useState } from 'react';
import { ArrowLeft, Phone, Search, MoreVertical, User, Smile, Paperclip, Image, Send } from 'lucide-react';
import { experts, chatMessages } from '../../../dados/chatMessages';

const ChatScreen = ({ setActiveScreen }) => {
    const [activeChat, setActiveChat] = useState('');
    const [message, setMessage] = useState('');
    const [showExperts, setShowExperts] = useState(true);
    
    const [activeMessagens, setActiveMessagens] = useState([]);

    function handleSwitchChat(expertId) {
      setActiveChat(expertId);
      setActiveMessagens(chatMessages.filter(msg => msg.expertId === expertId));
    }

    // Função para enviar mensagem
    const sendMessage = () => {
      if (message.trim() === '') return;
      setMessage('');
      const index = Math.max(...chatMessages.map(item => item.id)) + 1
      chatMessages.push({
        id: index,
        message: message,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        sender: 'user',
        expertId: activeChat
      })
      experts.find(expert => expert.id === activeChat).lastMessage = message;
      handleSwitchChat(activeChat);
    };
    
    // Componente para cada especialista na lista
    const ExpertItem = ({ expert, isActive, onClick }) => {
      if(expert === '') {
        return null;
      }
      return (
        <div 
          className={`max-w-[100%] p-3 flex items-center ${isActive ? 'bg-green-50' : 'hover:bg-gray-50'} rounded-xl cursor-pointer`}
          onClick={onClick}
        >
          <div className={`h-12 bg-${expert.avatar}-100 p-2 rounded-full flex items-center justify-center mr-3 relative`}>
            <User className={`text-${expert.avatar}-600`} />
            {expert.online && (
              <div className="absolute bottom-0 right-0 h-3 bg-green-500 rounded-full border-2 border-white"></div>
            )}
          </div>
          <div className="w-[80%] box-border flex-1">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-gray-800">{expert.name}</h4>
              <span className="text-xs text-gray-500">{expert.time}</span>
            </div>
            <p className="text-xs text-gray-500">{expert.role}</p>
            <p className="text-sm text-gray-600 truncate mt-1 max-w-[80%]">{expert.lastMessage.slice(0, 30) + "..."}</p>
          </div>
          {expert.unread > 0 && (
            <div className="ml-2 bg-green-500 p-2 text-white text-xs font-medium h-5 rounded-full flex items-center justify-center">
              {expert.unread}
            </div>
          )}
        </div>
    )};
    
    // Componente para cada mensagem do chat
    const ChatMessage = ({ message }) => (
      <div className={`flex mb-4 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
        {message.sender === 'expert' && (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2 mt-1">
            <User className="text-green-600" size={16} />
          </div>
        )}
        <div className={`max-w-[75%] ${message.sender === 'user' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-800'} rounded-xl p-3`}>
          <p className="text-sm">{message.message}</p>
          <div className={`text-xs mt-1 flex justify-end ${message.sender === 'user' ? 'text-green-100' : 'text-gray-500'}`}>
            {message.time}
          </div>
        </div>
      </div>
    );
    
    // Renderiza a lista de especialistas (painel esquerdo)
    const renderExpertsList = () => (
      <div className={`${showExperts ? 'block' : 'hidden'} md:block h-full border-r border-gray-200 p-4`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg text-gray-800">Especialistas</h3>
          <div className="flex items-center">
            <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
              <Search size={18} className="text-gray-500" />
            </button>
            <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
              <MoreVertical size={18} className="text-gray-500" />
            </button>
          </div>
        </div>
        
        <div className="overflow-y-auto max-h-[calc(100vh-200px)] max-w-[100%]">
          {experts.map(expert => (
            <ExpertItem 
              key={expert.id}
              expert={expert}
              isActive={activeChat === expert.id}
              onClick={() => {
                handleSwitchChat(expert.id);
                setShowExperts(false);
              }}
            />
          ))}
        </div>
      </div>
    );
    
    // Renderiza o chat ativo (painel direito)
    const renderActiveChat = () => {
      const activeExpert = experts.find(expert => expert.id === activeChat);
      if (!activeExpert) return null;
      
      return (
        <div className={`${!showExperts ? 'block' : 'hidden'} md:flex flex-1 flex flex-col h-full justify-between`}>
          {/* Cabeçalho do chat */}
          <div className="border-b border-gray-200 p-4 flex justify-between items-center">
            <div className="flex items-center">
              <button 
                className="md:hidden w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 mr-2"
                onClick={() => setShowExperts(true)}
              >
                <ArrowLeft size={18} className="text-gray-500" />
              </button>
              <div className={`w-10 h-10 bg-${activeExpert.avatar }-100 rounded-full flex items-center justify-center mr-3 relative`}>
                <User className={`text-${activeExpert.avatar }-600`} />
                {activeExpert.online && (
                  <div className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </div>
              <div>
                <h4 className="font-medium text-gray-800">{activeExpert.name}</h4>
                <p className="text-xs text-gray-500">{activeExpert.role}</p>
              </div>
            </div>
            <div className="flex">
              <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                <Phone size={18} className="text-green-600" />
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                <MoreVertical size={18} className="text-gray-500" />
              </button>
            </div>
          </div>
          {/* Corpo do chat (mensagens) */}
          <div className="flex-1 overflow-y-scroll h-[70%] p-4 bg-gray-50">
            {activeMessagens.map(msg => (
              <ChatMessage key={msg.id} message={msg} />
            ))}
          </div>
          
          {/* Input para enviar mensagem */}
          <div className="border-t border-gray-200 p-3 bg-white">
            <div className="flex items-center bg-gray-100 rounded-full px-4 p-2 justify-between">
              <button className="p-1 rounded-full hover:bg-gray-200">
                <Smile size={20} className="text-gray-500" />
              </button>
              <input
                type="text"
                placeholder="Digite sua mensagem..."
                className="flex-1 bg-transparent border-none outline-none px-3 py-1 w-3/4"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              />
              <div className="flex items-center">
                <button className="p-1 rounded-full hover:bg-gray-200">
                  <Paperclip size={20} className="text-gray-500" />
                </button>
                <button className="p-1 rounded-full hover:bg-gray-200">
                  <Image size={20} className="text-gray-500" />
                </button>
                <button 
                  className={`ml-2 p-2 rounded-full ${message.trim() ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'}`}
                  onClick={sendMessage}
                  disabled={!message.trim()}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    };
    
    return (
      <div className="h-[calc(100vh-120px)] flex flex-col overflow-y-hidden box-border max-h-[100%]">
        <div className="flex items-center p-4">
          <ArrowLeft 
            size={20} 
            className="mr-2 cursor-pointer" 
            onClick={() => setActiveScreen('home')} 
          />
          <h2 className="text-xl font-bold text-gray-800">Chat com Especialistas</h2>
        </div>
        
        <div className="flex-1 flex flex-col md:flex-row overflow-y-hidden">
          {renderExpertsList()}
          {renderActiveChat()}
        </div>
      </div>
    );
  };

export default ChatScreen;